/* eslint-disable camelcase */
import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import classNames from "classnames";
import GoogleMap from "google-map-react";
import { GOOGLE_MAP_SETTINGS, GOOGLE_MAP_OPTIONS } from "@constants";
import { OfficerService, JobService } from "@services";
import utils from "@utils";
import { Image, MapMarker } from "../../../components";

import "./JobLocationDetails.scss";

let cancelOfficerRequest;
let cancelMapPointsRequest;
class JobLocationDetails extends Component {
	constructor(props) {
		super(props);

		this.state = {
			officer: {},
			jobData: {},
			mapPoints: [],
			filteredPoints: [],
			filter: "all",
			mapCenter: {},
			highlightedPoint: "",
			tabOptions: [],
			message: "",
		};
	}

	componentDidMount() {
		const {
			router,
			params: { jobId },
			location: {
				query: { positionId, userId },
			},
		} = this.props;

		if (!positionId || !userId) router.push(`/jobs/${jobId}`);
		else {
			this.getOfficer();
			this.getMapPoints();
		}
	}

	componentWillUnmount() {
		if (cancelOfficerRequest) cancelOfficerRequest();
		if (cancelMapPointsRequest) cancelMapPointsRequest();
	}

	/** Retrieves officer information based on the `userId` provided in query parameters */
	getOfficer = () => {
		const {
			location: {
				query: { userId },
			},
		} = this.props;
		const { lang } = global;
		if (cancelOfficerRequest !== undefined) cancelOfficerRequest();

		OfficerService.getById(userId, { includeStats: true }, c => {
			cancelOfficerRequest = c;
		})
			.then(({ data: officer }) => {
				cancelOfficerRequest = undefined;
				this.setState({ officer });
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					utils.notify({
						type: "error",
						message: lang.t("Error loading officer"),
						timeOut: 2000,
					});
				}
			});
	};

	/**
	 * Retrieves a list of check-ins and pings for an officer working the job.
	 * Officer and position Id are both provided in query parameters
	 */
	getMapPoints = () => {
		const {
			location: {
				query: { positionId, userId },
			},
		} = this.props;
		const { lang } = global;
		if (cancelMapPointsRequest !== undefined) cancelMapPointsRequest();

		JobService.getCheckIns(positionId, userId, c => {
			cancelMapPointsRequest = c;
		})
			.then(({ data }) => {
				cancelMapPointsRequest = undefined;
				const { CheckIns, JobLocation, StartDateTime, EndDateTime } = data;
				const checkins = CheckIns.filter(item => item.IsCheckin).map(item => ({
					...item,
					type: "checkin",
					name: lang.t("Clocked In"),
				}));
				const checkouts = CheckIns.filter(item => item.CheckoutLatitude !== null).map(
					item => ({ ...item, type: "checkout", name: lang.t("Clocked Out") }),
				);
				const pings = CheckIns.filter(item => item.Type === 1).map(item => ({
					...item,
					type: "ping",
					name: lang.t("Ping"),
				}));

				const tabOptions = [{ filter: "all", label: lang.t("All") }];
				if (checkins.length > 0 || checkouts.length > 0) {
					tabOptions.push({ filter: "checkin", label: lang.t("Clock In/Out") });
				}
				if (pings.length > 0) {
					tabOptions.push({ filter: "ping", label: lang.t("Pings") });
				}

				const jobData = {
					name: JobLocation.Name,
					startUTC: `${StartDateTime}Z`,
					endUTC: `${EndDateTime}Z`,
					location: { lat: JobLocation.Latitude, lng: JobLocation.Longitude },
				};

				const pointsArray = [
					{ type: "job", name: jobData.name, location: jobData.location },
					...checkins,
					...pings,
					...checkouts,
				];

				const mapPoints = pointsArray.map((item, index) => {
					if (item.type === "job") return { ...item, id: "Job Location" };

					const result = {
						id: `jobpoint-${index}`,
						type: item.type,
						name: item.name,
						responded: item.PingRequestStatus !== 3,
						errorMsg: item.PingErros?.length > 0 ? item.PingErros[0] : "",
						dateTimeUTC: `${item.CheckinDateTimeUTC}Z`,
						location: { lat: item.Latitude, lng: item.Longitude },
						distance: item.DistanceFromJob,
					};

					if (item.type === "checkout") {
						result.dateTimeUTC = `${item.CheckoutDateTimeUTC}Z`;
						result.location = {
							lat: item.CheckoutLatitude,
							lng: item.CheckoutLongitude,
						};
						result.distance = item.DistanceFromJobCheckOut;
					}

					return result;
				});

				this.setState({
					mapPoints,
					filteredPoints: mapPoints,
					tabOptions,
					jobData,
					mapCenter: jobData.location,
				});
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					utils.notify({
						type: "error",
						message: lang.t("Error loading Job Points"),
						timeOut: 2000,
					});
				}
			});
	};

	/**
	 * @param {Object} obj
	 * @param {String} obj.type What the map marker is indicating
	 * @param {String} obj.dateTimeUTC A UTC timestamp for the action indicated by the marker
	 * @returns {String} a human-readable timestamp for the action
	 */
	buildMapMarkerMessage = ({ type, dateTimeUTC }) => {
		const {
			user: { TimeZoneId },
		} = this.props;
		if (type === "job") return null;
		return utils.formatDate(new Date(dateTimeUTC), "en-US", TimeZoneId, "dt-short");
	};

	/**
	 * Changes which map markers appear on screen based on the user's selection
	 * @param {String} filter which map markers to show on screen
	 */
	filterPoints = filter => {
		const { jobData, mapPoints } = this.state;
		let mapCenter = GOOGLE_MAP_SETTINGS.defaultCenter;
		let filteredPoints = mapPoints;

		if (filter === "all") {
			mapCenter = { ...jobData.location };
		} else if (filter === "checkin") {
			filteredPoints = mapPoints.filter(point => point.type !== "ping");
		} else {
			filteredPoints = mapPoints.filter(
				point => point.type === "job" || point.type === "ping",
			);
		}

		if (mapPoints.findIndex(point => point.type === filter) !== -1) {
			mapCenter = mapPoints.find(point => point.type === filter).location;
		}

		this.setState({ filter, mapCenter, filteredPoints });
	};

	highlightPoint =
		point =>
		({ key }) => {
			const {
				user: { TimeZoneId },
			} = this.props;
			if (!key || (key && key === "Enter")) {
				this.setState({
					mapCenter: point.location,
					highlightedPoint: point.id,
					message:
						utils.formatDate(
							new Date(point?.dateTimeUTC),
							"en-US",
							TimeZoneId,
							"dt-short",
						) || null,
				});
			}
		};

	render() {
		const { lang } = global;
		const {
			officer: { ContactViewModel, JurisdictionModel, OfficerStatisticsModel, ...officer },
			jobData,
			mapPoints,
			filteredPoints,
			filter,
			mapCenter,
			highlightedPoint,
			tabOptions,
			message,
		} = this.state;
		const {
			user: { TimeZoneId },
			router,
		} = this.props;

		const clockInUTC = mapPoints.find(point => point.type === "checkin")?.dateTimeUTC;
		const clockOutUTC = mapPoints.find(point => point.type === "checkout")?.dateTimeUTC;

		return (
			<Fragment>
				<div
					className="container-fluid"
					id="ld-subnav"
				>
					<div className="row">
						<div className="col-sm-12">
							<button
								type="button"
								id="go-back"
								className="btn btn-link"
								onClick={() => router.goBack()}
							>
								<i className="rkf rkf-chevron-left" />
								{lang.t("Back")}
							</button>
						</div>
					</div>
				</div>
				<div
					className="container"
					id="location-details"
				>
					<div className="row">
						<div className="col-sm-12">
							<h1>{`${lang.t("Job")} ${lang.t("Activity")}`}</h1>
						</div>
					</div>
					<div className="row">
						<div className="col-xs-4 col-sm-2">
							<Image
								className="officer-image img-circle"
								src={officer.ProfilePhotoUrl}
								fallbackSrc="avatar"
							/>
						</div>
						<div className="col-xs-8 col-sm-4 col-md-5">
							<h4 className="truncate">
								{ContactViewModel?.FirstName || ""}{" "}
								{ContactViewModel?.LastName || ""}
							</h4>
							<div>
								{officer.OfficerRank || ""}
								<br className="visible-xs" />
								<span className="hidden-xs">
									{officer.OfficerRank && JurisdictionModel?.Name ? ", " : ""}
								</span>
								{JurisdictionModel?.Name || ""}
							</div>
						</div>
						<div className="col-xs-12 col-sm-6 col-md-4 col-md-offset-1 job-time-info">
							<div className="row">
								<div className="col-xs-6">
									<strong>{lang.t("Scheduled Time")}</strong>
									<div>
										{utils.formatTime(
											new Date(jobData.startUTC),
											true,
											TimeZoneId,
										)}
										&nbsp;-&nbsp;
										{utils.formatTime(
											new Date(jobData.endUTC),
											true,
											TimeZoneId,
										)}
									</div>
								</div>
								<div className="col-xs-6">
									<strong>{lang.t("Clock In/Out")}</strong>
									<div>
										{utils.formatTime(new Date(clockInUTC), true, TimeZoneId)}
										&nbsp;-&nbsp;
										{utils.formatTime(new Date(clockOutUTC), true, TimeZoneId)}
									</div>
								</div>
							</div>
						</div>
					</div>
					<div
						className="separator separator-solid"
						id="above-map"
					/>
					<div className="row">
						<div className="col-sm-6 col-md-7">
							<div className="pingmap-container">
								<GoogleMap
									zoom={15}
									center={mapCenter}
									bootstrapURLKeys={{ key: GOOGLE_MAP_SETTINGS.key }}
									options={GOOGLE_MAP_OPTIONS}
								>
									<For
										each="marker"
										of={filteredPoints}
									>
										<MapMarker
											key={marker.id}
											type={marker.type}
											lat={marker.location?.lat || 0}
											lng={marker.location?.lng || 0}
											highlighted={highlightedPoint === marker.id}
											message={message}
										/>
									</For>
								</GoogleMap>
							</div>
						</div>
						<div className="col-sm-6 col-md-4 col-md-offset-1">
							<div className="row tabs">
								<For
									each="tab"
									of={tabOptions}
								>
									<div
										key={`ping-tab-${tab.filter}`}
										className={`col-xs-${12 / tabOptions.length}`}
									>
										<button
											type="button"
											className={classNames("btn btn-link btn-block", {
												active: filter === tab.filter,
											})}
											onClick={() => this.filterPoints(tab.filter)}
										>
											{tab.label}
										</button>
									</div>
								</For>
							</div>
							<div className="container-fluid point-list">
								<For
									each="point"
									of={filteredPoints}
								>
									<div
										key={`list-${point.id}`}
										className="row"
										tabIndex="0"
										role="button"
										onClick={this.highlightPoint(point)}
										onKeyPress={this.highlightPoint(point)}
									>
										<div className="col-xs-2">
											<span className={`map-marker ${point.type}-marker`} />
										</div>
										<div className="col-xs-10">
											<Choose>
												<When condition={point.type === "job"}>
													<strong>{lang.t("Job Site")}</strong>
													<div className="truncate">{point.name}</div>
												</When>
												<Otherwise>
													<div>
														<If
															condition={
																point.type === "checkin" ||
																point.type === "checkout"
															}
														>
															<strong>{point.name}:&nbsp;</strong>
														</If>
														{utils.formatDate(
															new Date(point.dateTimeUTC),
															"en-US",
															TimeZoneId,
															"dt-num",
														)}
													</div>
													<div>
														<Choose>
															{/* status of 3 indicates no response from the device */}
															<When condition={point.responded === 3}>
																<strong>
																	{lang.t("No response")}
																</strong>
															</When>
															<When condition={point.errorMsg}>
																<strong>{point.errorMsg}</strong>
															</When>
															<Otherwise>
																{/* eslint-disable-next-line camelcase */}
																{lang.t(
																	"dynamic.miles from job site",
																	{
																		smart_count:
																			point.distance.toFixed(
																				2,
																			),
																	},
																)}
															</Otherwise>
														</Choose>
													</div>
												</Otherwise>
											</Choose>
										</div>
									</div>
								</For>
							</div>
						</div>
					</div>
				</div>
			</Fragment>
		);
	}
}

JobLocationDetails.propTypes = {
	user: PropTypes.object,
	router: PropTypes.object,
	location: PropTypes.object,
	params: PropTypes.object,
};

JobLocationDetails.defaultProps = {
	user: {},
	router: {},
	location: {
		positionId: "",
		userId: "",
	},
	params: { jobId: "" },
};

export default JobLocationDetails;
